export interface ChatDisplayNameInput {
  displayName?: string | null;
  firstName?: string | null;
  name?: string | null;
  lastName?: string | null;
  lastname?: string | null;
  fullName?: string | null;
  senderName?: string | null;
  fallback?: string;
}

export function formatChatDisplayName(input: ChatDisplayNameInput): string {
  const fallback = normalizeWhitespace(input.fallback) || '';
  const displayName = normalizeWhitespace(input.displayName);
  if (displayName) return displayName;
  const first = firstToken(input.firstName ?? input.name);
  const last = firstToken(input.lastName ?? input.lastname);

  if (first && last) return `${first} ${last}`;
  if (first) return first;

  const fullName = normalizeWhitespace(input.fullName ?? input.senderName);
  if (fullName) return compactFullName(fullName);

  return fallback;
}

/**
 * Resolves a group message sender name exactly like the contact list:
 * the live canonical displayName (participant snapshot displayName ??
 * identity-cache displayName) wins over the stale sender.fullName snapshot.
 * Callers may also use this precedence in read projections so historical
 * message records remain immutable while their visible sender identity stays
 * current.
 */
export interface LiveChatSenderNameInput {
  participantDisplayName?: string | null;
  cachedDisplayName?: string | null;
  fullName?: string | null;
  senderName?: string | null;
  fallback?: string;
}

export function resolveLiveChatSenderName(input: LiveChatSenderNameInput): string {
  const live =
    normalizeWhitespace(input.participantDisplayName) ||
    normalizeWhitespace(input.cachedDisplayName);
  return formatChatDisplayName({
    displayName: live || undefined,
    fullName: input.fullName,
    senderName: input.senderName,
    fallback: input.fallback,
  });
}

function compactFullName(fullName: string): string {
  const tokens = fullName.split(/\s+/);

  // Best-effort fallback when the backend only provides a full name:
  // 4+ tokens commonly means two given names + two surnames, so use first + third.
  if (tokens.length >= 4) return `${tokens[0]} ${tokens[2]}`;
  // With 3 tokens the boundary is unknown; prefer first + last to keep one likely surname.
  if (tokens.length === 3) return `${tokens[0]} ${tokens[2]}`;
  return tokens.join(' ');
}

function firstToken(value: string | null | undefined): string {
  return normalizeWhitespace(value).split(/\s+/)[0] ?? '';
}

function normalizeWhitespace(value: string | null | undefined): string {
  return value?.trim().replace(/\s+/g, ' ') ?? '';
}
